/**
 * API Service for communicating with the Data Processing FastAPI backend
 */

import axios from 'axios';
import dataTransformService from './dataTransformService';

// Import the server base URL from UserContext to ensure consistency
import { server_base_url } from '../context/UserContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || server_base_url;
const API_KEY = process.env.REACT_APP_API_KEY || 'your_secure_api_key';

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': API_KEY
  },
  // Short timeout to prevent UI hanging
  timeout: 10000, // 10 seconds default timeout
});

// Helper function to transform NDVI data from the API
const transformNDVIData = (data) => {
  // If the data is already in the format we need, return it
  if (data.value !== undefined) {
    return data;
  }
  
  // Convert backend format to frontend format
  return {
    value: data.current_value || 0,
    min: data.min_value || 0,
    max: data.max_value || 1,
    healthStatus: getHealthStatus(data.current_value),
    lastUpdated: data.timestamp || new Date().toISOString()
  };
};

// Helper function to determine health status based on NDVI value
const getHealthStatus = (ndviValue) => {
  if (!ndviValue && ndviValue !== 0) return 'unknown';
  if (ndviValue > 0.7) return 'excellent';
  if (ndviValue > 0.5) return 'good';
  if (ndviValue > 0.3) return 'fair';
  return 'poor';
};

// Add request interceptor for debugging API calls
axiosInstance.interceptors.request.use(request => {
  console.log('API Request:', request.method, request.url);
  return request;
});

// Add response interceptor for debugging API responses
axiosInstance.interceptors.response.use(
  response => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  error => {
    console.error('API Error:', error.response ? error.response.status : error.message);
    return Promise.reject(error);
  }
);

// API service for satellite data and farm analysis
const apiService = {
  /**
   * Perform a health check to verify API connectivity
   * @returns {Promise<boolean>} True if API is healthy, false otherwise
   */
  async healthCheck() {
    try {
      const response = await axiosInstance.get('/health');
      return response.status === 200;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  },

  /**
   * Get the list of farmers
   * @returns {Promise<Array>} List of farmers
   */
  async getFarmers() {
    try {
      const response = await axiosInstance.get('/api/v1/farmers/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch farmers:', error);
      throw new Error('Failed to fetch farmers data from API');
    }
  },

  /**
   * Get farm list
   * @returns {Promise<Array>} List of farms
   */
  async getFarms() {
    try {
      console.log('Fetching farms list...');
      const response = await axiosInstance.get('/api/v1/farms/');
      console.log('Farms API response:', response.data);
      
      // Extract farms array from the response
      const farmsData = response.data.farms || [];
      
      return farmsData.map(farm => ({
        id: farm.id,
        name: farm.name || `Farm #${farm.id}`,
        // Extract lat/lng from the first coordinate in the GeoJSON boundary
        location: farm.boundary ? {
          lat: farm.boundary.coordinates[0][0][1],  // Latitude is second value
          lng: farm.boundary.coordinates[0][0][0]   // Longitude is first value
        } : { lat: 0, lng: 0 },
        // Preserve original field names for consistency
        crop_type: farm.crop_type || 'Unknown',
        area_hectares: farm.area_hectares || 0,
        // Also add renamed versions for backward compatibility
        cropType: farm.crop_type || 'Unknown',
        farmSize: farm.area_hectares || 0,
        // Pass the full boundary for map rendering
        boundary: farm.boundary,
        // Add owner_id for farmer selection
        ownerId: farm.owner_id,
        owner_id: farm.owner_id
      }));
    } catch (error) {
      console.error('Error fetching farms:', error);
      throw error;
    }
  },

  /**
   * Get farm details by ID
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Farm details
   */
  async getFarmById(farmId) {
    try {
      const response = await axiosInstance.get(`/api/v1/farms/${farmId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching farm ${farmId}:`, error);
      throw error;
    }
  },

  /**
   * Get NDVI data for a farm
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} NDVI data
   */
  async getFarmNDVI(farmId) {
    try {
      console.log(`Fetching NDVI data for farm ${farmId}`);
      const response = await axiosInstance.get(`/api/v1/farms/${farmId}/ndvi`);
      console.log(`NDVI response for farm ${farmId}:`, response.data);
      
      // Validate farm ID in response if available
      if (response.data && response.data.farm_id && response.data.farm_id !== farmId) {
        console.warn(`NDVI response has mismatched farm ID. Expected: ${farmId}, Received: ${response.data.farm_id}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching NDVI for farm ${farmId}:`, error);
      throw error;
    }
  },

  /**
   * Get yield forecast for a farm
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Yield forecast data
   */
  async getFarmYieldForecast(farmId) {
    try {
      console.log(`Fetching yield forecast for farm ${farmId}`);
      const response = await axiosInstance.get(`/api/v1/farms/${farmId}/yield-forecast`);
      console.log(`Yield forecast response for farm ${farmId}:`, response.data);
      
      // Validate farm ID in response if available
      if (response.data && response.data.farm_id && response.data.farm_id !== farmId) {
        console.warn(`Yield forecast response has mismatched farm ID. Expected: ${farmId}, Received: ${response.data.farm_id}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching yield forecast for farm ${farmId}:`, error);
      throw error;
    }
  },

  /**
   * Get risk assessment for a farm
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Risk assessment data
   */
  async getFarmRiskAssessment(farmId) {
    try {
      console.log(`Fetching risk assessment for farm ${farmId}`);
      const response = await axiosInstance.get(`/api/v1/farms/${farmId}/risk-assessment`);
      
      // Enhanced logging to debug the risk assessment API response
      console.log(`Risk assessment response for farm ${farmId}:`, response.data);
      console.log(`Risk assessment response type:`, typeof response.data);
      console.log(`Risk assessment keys:`, response.data ? Object.keys(response.data) : 'No data');
      
      // If the response is empty or not an object, log a clearer error
      if (!response.data || typeof response.data !== 'object') {
        console.error(`Invalid risk assessment response format for farm ${farmId}:`, response.data);
        return null;
      }
      
      // Validate farm ID in response if available
      if (response.data.farm_id && response.data.farm_id !== farmId) {
        console.warn(`Risk assessment response has mismatched farm ID. Expected: ${farmId}, Received: ${response.data.farm_id}`);
        // Set the correct farm ID but don't alter other data
        response.data.farm_id = farmId;
      }
      
      // Add farm_id to the response if not present
      if (!response.data.farm_id) {
        response.data.farm_id = farmId;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching risk assessment for farm ${farmId}:`, error);
      throw error;
    }
  },

  /**
   * Get full analysis for a farm by combining multiple API calls
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Combined farm analysis data
   */
  async getFarmFullAnalysis(farmId) {
    try {
      console.log(`Getting full analysis for farm ${farmId}`);
      
      // Get basic farm details first
      console.log(`Fetching basic farm details for ${farmId}`);
      const farmDetails = await this.getFarmById(farmId);
      console.log('Farm details retrieved:', farmDetails ? 'Success' : 'Failed');
      
      // Get NDVI data sequentially
      console.log(`Fetching NDVI data for ${farmId}`);
      let ndviData = null;
      try {
        ndviData = await this.getFarmNDVI(farmId);
        console.log('NDVI data retrieved successfully');
      } catch (error) {
        console.error(`Failed to get NDVI data for ${farmId}:`, error);
      }
      
      // Get yield forecast sequentially
      console.log(`Fetching yield forecast for ${farmId}`);
      let yieldForecast = null;
      try {
        yieldForecast = await this.getFarmYieldForecast(farmId);
        console.log('Yield forecast retrieved successfully');
      } catch (error) {
        console.error(`Failed to get yield forecast for ${farmId}:`, error);
      }
      
      // Get risk assessment sequentially
      console.log(`Fetching risk assessment for ${farmId}`);
      let riskAssessment = null;
      try {
        riskAssessment = await this.getFarmRiskAssessment(farmId);
        console.log('Risk assessment retrieved successfully');
        console.log('Risk assessment data:', riskAssessment);
      } catch (error) {
        console.error(`Failed to get risk assessment for ${farmId}:`, error);
      }
      
      // Combine all data into a single object before transformation
      const combinedApiData = {
        ...farmDetails,
        ndvi: ndviData,
        yieldForecast: yieldForecast,
        riskAssessment: riskAssessment
      };
      
      // Detailed logging of what was retrieved from each endpoint
      console.log('Data retrieval summary:');
      console.log('- Farm details:', farmDetails ? 'Success' : 'Failed');
      console.log('- NDVI data:', ndviData ? 'Success' : 'Failed');
      console.log('- Yield forecast:', yieldForecast ? 'Success' : 'Failed');
      console.log('- Risk assessment:', riskAssessment ? 'Success' : 'Failed');
      
      // If we have risk assessment data, log its contents
      if (riskAssessment) {
        console.log('Risk assessment data being passed to transform:', riskAssessment);
      }
      
      // Transform the combined data
      const combinedData = dataTransformService.transformFarmData(combinedApiData);
      
      return combinedData;
    } catch (error) {
      console.error(`Failed to get full analysis for farm ${farmId}:`, error);
      throw new Error(`Failed to get complete analysis for farm ${farmId}`);
    }
  },

  /**
   * Get satellite imagery for a specific farm
   * @param {string} farmId - Farm ID
   * @param {Object} options - Options for satellite imagery
   * @param {string} options.date - Date for imagery (YYYY-MM-DD)
   * @param {string} options.visualization - Type of visualization (true-color, false-color, ndvi)
   * @param {boolean} options.backgroundProcessing - Process in background if intensive
   * @returns {Promise<Object>} Satellite imagery data or task ID
   */
  async getFarmSatelliteImagery(farmId, options = {}) {
    const { 
      date = null, 
      visualization = 'true-color', 
      backgroundProcessing = true
    } = options;
    
    // Longer timeout for satellite imagery
    const satelliteAxios = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000, // 60 second timeout
      headers: {
        'X-API-Key': API_KEY
      }
    });
    
    try {
      console.log(`Getting satellite imagery for farm ${farmId} with visualization ${visualization}`);
      
      const params = {
        visualization,
        background_processing: backgroundProcessing
      };
      
      if (date) {
        params.date = date;
      }
      
      // Make the request with the longer timeout
      const response = await satelliteAxios.get(`/api/v1/satellite/${farmId}`, { params });
      
      // Check if we got a task ID for background processing
      if (response.data && response.data.task_id) {
        console.log(`Background processing initiated with task ID ${response.data.task_id}`);
        return {
          status: 'processing',
          task_id: response.data.task_id,
          message: 'Processing satellite imagery in the background',
          metadata: {
            farm_id: farmId,
            date: date || 'latest',
            visualization: visualization
          }
        };
      }
      
      // Got the data directly
      return {
        status: 'success',
        data: response.data.image_data || response.data,
        message: 'Retrieved satellite imagery',
        metadata: {
          farm_id: farmId,
          date: date || 'latest',
          visualization: visualization
        }
      };
    } catch (error) {
      console.error(`Failed to get satellite imagery for ${farmId}:`, error);
      
      // Since the user specifically said satellite imagery can still use mock data,
      // we'll just return an error status without mock data
      return {
        status: 'error',
        data: null,
        message: 'Failed to retrieve satellite imagery',
        metadata: {
          farm_id: farmId,
          date: date || 'latest',
          visualization: visualization,
          error: error.message
        }
      };
    }
  },
  
  /**
   * Create a new farmer
   * @param {Object} farmerData - Farmer data
   * @returns {Promise<Object>} Created farmer
   */
  async createFarmer(farmerData) {
    try {
      const response = await axiosInstance.post('/api/v1/farmers/', farmerData);
      return response.data;
    } catch (error) {
      console.error('Failed to create farmer:', error);
      throw new Error(`Failed to create farmer: ${error.message}`);
    }
  },

  /**
   * Create a new farm
   * @param {Object} farmData - Farm data
   * @returns {Promise<Object>} Created farm
   */
  async createFarm(farmData) {
    console.log('Creating new farm with data: ', farmData);
    try {
      const response = await axiosInstance.post('/api/v1/farms/', farmData);
      console.log('Farm created successfully: ', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to create farm:', error);
      throw new Error(`Failed to create farm: ${error.message}`);
    }
  },
  /**
   * Get dashboard metrics for the overview page
   * @returns {Promise<Object>} Dashboard metrics data including monitored area, health index, and latest satellite update
   */
  async getDashboardMetrics() {
    try {
      // First attempt: Try standard FastAPI endpoint
      try {
        const response = await axiosInstance.get('/api/v1/dashboard-metrics/');
        return {
          monitoredLandArea: response.data.monitored_land_area,
          healthIndex: response.data.health_index,
          latestSatelliteUpdate: response.data.latest_satellite_update
        };
      } catch (firstError) {
        console.log('First endpoint attempt failed, trying Django format');
        // Second attempt: Try Django format endpoint
        const response = await axiosInstance.get('/api/get_dashboard_metrics/');
        return {
          monitoredLandArea: response.data.monitored_land_area,
          healthIndex: response.data.health_index,
          latestSatelliteUpdate: response.data.latest_satellite_update
        };
      }
    } catch (error) {
      console.error('Failed to fetch dashboard metrics from all endpoints:', error);
      // For now, return demo values so the UI doesn't break
      return {
        monitoredLandArea: { value: 245.8, unit: 'hectares', count: 7, status: 'success' },
        healthIndex: { value: 0.68, status: 'good', trend: 'stable', samples: 12 },
        latestSatelliteUpdate: { date: '2025-06-15', days_ago: 7, status: 'warning', image_count: 34 }
      };
    }
  },

  /**
   * Get farm boundary geometry by ID
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Farm geometry data with coordinates
   */
  async getFarmGeometry(farmId) {
    try {
      const response = await axiosInstance.get(`/api/v1/boundaries/farm-geometry/${farmId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch farm geometry:', error);
      
      // Return fallback data if API call fails in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Returning mock farm geometry data as fallback');
        return {
          "farm_id": farmId,
          "name": "gigi farm",
          "type": "Polygon",
          "coordinates": [
            [-34.00256677544119, 19.491489831450753],
            [-34.00345620472669, 19.49157566213923],
            [-34.003349473704205, 19.492433969023995],
            [-34.00256677544119, 19.49230522299128],
            [-34.00256677544119, 19.491489831450753]
          ]
        };
      }
      
      throw new Error('Failed to fetch farm geometry data');
    }
  },
  
  /**
   * Get farm boundary geometry by ID
   * @param {string} farmId - Farm ID
   * @returns {Promise<Object>} Farm geometry data with coordinates
   */
  async getFarmGeometry(farmId) {
    try {
      const response = await axiosInstance.get(`/api/v1/boundaries/farm-geometry/${farmId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch farm geometry:', error);
      
      // Return fallback data if API call fails in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Returning mock farm geometry data as fallback');
        return {
          "farm_id": farmId,
          "name": "gigi farm",
          "type": "Polygon",
          "coordinates": [
            [-34.00256677544119, 19.491489831450753],
            [-34.00345620472669, 19.49157566213923],
            [-34.003349473704205, 19.492433969023995],
            [-34.00256677544119, 19.49230522299128],
            [-34.00256677544119, 19.491489831450753]
          ]
        };
      }
      
      throw new Error('Failed to fetch farm geometry data');
    }
  },

  /**
   * Get list of all farms
   * @returns {Promise<Array>} List of farms with their details
   */
  async getFarms() {
    try {
      const response = await axiosInstance.get('/api/v1/farms/');
      // If response.data has a farms property, return that; otherwise return response.data directly
      return response.data.farms || response.data;
    } catch (error) {
      console.error('Failed to fetch farms:', error);
      
      // Return fallback data if API call fails in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Returning mock farms data as fallback');
        // Return the array directly, not an object containing the array
        return [
          {
            id: "1c86886e-3bbf-4644-882c-2e3a885a03b2",
            name: "gigi farm",
            boundary: {
              type: "Polygon",
              coordinates: [
                [
                  [19.491489831, -34.002566775],
                  [19.491575662, -34.003456205],
                  [19.492433969, -34.003349474],
                  [19.492305223, -34.002566775],
                  [19.491489831, -34.002566775]
                ]
              ]
            },
            crop_type: "Coffee",
            owner_id: "33a19bef-6c05-4b48-b156-0bf63a34edb8",
            area_hectares: 0.7308579435564024,
            created_at: "2025-06-22T13:55:48.498517",
            updated_at: "2025-06-22T13:55:48.498530",
            metadata: null
          },
          {
            id: "e65a3ae2-b06d-4363-8e1d-9c971d3b2125",
            name: "MK",
            boundary: {
              type: "Polygon",
              coordinates: [
                [
                  [30.407563281, -29.645637996],
                  [30.363617969, -29.320475882],
                  [29.968110156, -29.607437829],
                  [30.407563281, -29.645637996]
                ]
              ]
            },
            crop_type: "Cassava",
            owner_id: "5478fb01-bc86-46d3-a781-70f882022ffe",
            area_hectares: 75949.59870098448,
            created_at: "2025-06-22T13:50:11.990255",
            updated_at: "2025-06-22T13:50:11.990263",
            metadata: null
          }
        ];
      }
      
      throw new Error('Failed to fetch farms data');
    }
  }
};

export default apiService;
